;(function (global, factory) {
  const api = factory(global);

  global.ReturnsPortalApp = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (global) {
  const helpers = global.ReturnsPortalHelpers;
  const DRAFT_KEY_PREFIX = 'returns-portal:v1';
  const DRAFT_TTL_MS = 2 * 60 * 60 * 1000;

  function uniqueId(prefix) {
    return prefix + '-' + Math.random().toString(36).slice(2, 11);
  }

  function getErrorMessage(error, fallbackMessage) {
    if (error && typeof error === 'object' && error.message) {
      return error.message;
    }

    return fallbackMessage;
  }

  function normalizeString(value) {
    return String(value || '').trim();
  }

  function getNow() {
    if (typeof global.__returnsPortalNow === 'function') {
      return Number(global.__returnsPortalNow());
    }

    if (typeof global.__returnsPortalNow === 'number') {
      return Number(global.__returnsPortalNow);
    }

    return Date.now();
  }

  function createPortal() {
    return {
      config: null,
      reasons: [],
      reasonsById: {},
      order: null,
      detailedOrder: null,
      items: [],
      requestId: '',
      formOrderNumber: '',
      formZipCode: '',
      currentStep: 1,
      lookupLoading: false,
      restoreLoading: false,
      detailLoading: false,
      submitLoading: false,
      step2Attempted: false,
      lookupError: '',
      detailError: '',
      country: '',
      shippingChoice: 'need',
      makeInitialShippingZero: false,
      activeDraftKey: '',
      watchersInitialized: false,
      syncSuspended: false,

      async boot(config) {
        this.config = config || {};
        this.reasons = helpers.buildReasons(
          this.config.mode,
          this.config.rawReasons || ''
        );
        this.reasonsById = helpers.createReasonLookup(this.reasons);
        this.currentStep = 1;
        this.installWatchers();
        await this.restoreFromBrowser();
      },

      refreshItems() {
        this.items = this.items.slice();
      },

      installWatchers() {
        if (this.watchersInitialized || typeof this.$watch !== 'function') {
          return;
        }

        this.watchersInitialized = true;

        this.$watch('formOrderNumber', () => {
          this.handleLookupFieldChange();
        });

        this.$watch('formZipCode', () => {
          this.handleLookupFieldChange();
        });

        this.$watch('currentStep', (nextStep, previousStep) => {
          if (this.syncSuspended) {
            return;
          }

          this.syncUrlState();
          this.persistDraft();
          this.scrollPortalToTop(nextStep, previousStep);
        });

        this.$watch('shippingChoice', () => {
          if (this.syncSuspended) {
            return;
          }

          this.persistDraft();
        });

        this.$watch('items', () => {
          if (this.syncSuspended) {
            return;
          }

          this.persistDraft();
        });
      },

      getStorage() {
        try {
          return global.localStorage || null;
        } catch (error) {
          return null;
        }
      },

      buildDraftKey(orderNumber, zip) {
        return helpers.createPortalDraftKey(
          DRAFT_KEY_PREFIX,
          this.config?.mode || 'return',
          orderNumber,
          zip
        );
      },

      getCurrentDraftKey() {
        return this.buildDraftKey(this.formOrderNumber, this.formZipCode);
      },

      scrollPortalToTop(nextStep, previousStep) {
        if (
          !Number.isFinite(Number(nextStep)) ||
          Number(nextStep) === Number(previousStep)
        ) {
          return;
        }

        const performScroll = () => {
          const portalRoot = this.$root || this.$el;

          if (!portalRoot || typeof portalRoot.getBoundingClientRect !== 'function') {
            return;
          }

          const portalTop =
            portalRoot.getBoundingClientRect().top +
            (global.pageYOffset || global.scrollY || 0) -
            24;

          if (typeof global.scrollTo === 'function') {
            global.scrollTo({
              top: Math.max(portalTop, 0),
              behavior: 'smooth',
            });
            return;
          }

          portalRoot.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        };

        if (typeof this.$nextTick === 'function') {
          this.$nextTick(performScroll);
          return;
        }

        if (typeof global.requestAnimationFrame === 'function') {
          global.requestAnimationFrame(performScroll);
          return;
        }

        performScroll();
      },

      readUrlState() {
        if (!global.location) {
          return {
            orderNumber: '',
            zip: '',
            step: 1,
            hasLookupValues: false,
          };
        }

        const params = new URLSearchParams(global.location.search || '');
        const orderNumber = params.get('order_number') || '';
        const zip = params.get('zip') || '';

        return {
          orderNumber: orderNumber,
          zip: zip,
          step: helpers.sanitizePortalStep(params.get('step')),
          hasLookupValues: Boolean(normalizeString(orderNumber) || normalizeString(zip)),
        };
      },

      syncUrlState() {
        if (!global.location || !global.history?.replaceState) {
          return;
        }

        const url = new URL(global.location.href);
        const orderNumber = normalizeString(this.formOrderNumber);
        const zip = normalizeString(this.formZipCode);

        if (orderNumber) {
          url.searchParams.set('order_number', orderNumber);
        } else {
          url.searchParams.delete('order_number');
        }

        if (zip) {
          url.searchParams.set('zip', zip);
        } else {
          url.searchParams.delete('zip');
        }

        if (orderNumber || zip || this.currentStep > 1) {
          url.searchParams.set(
            'step',
            String(helpers.sanitizePortalStep(this.currentStep))
          );
        } else {
          url.searchParams.delete('step');
        }

        const nextUrl =
          url.pathname +
          (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') +
          url.hash;

        global.history.replaceState({}, '', nextUrl);
      },

      handleLookupFieldChange() {
        if (this.syncSuspended) {
          return;
        }

        this.syncUrlState();

        if (!this.activeDraftKey) {
          return;
        }

        const currentDraftKey = this.getCurrentDraftKey();

        if (currentDraftKey && currentDraftKey === this.activeDraftKey) {
          return;
        }

        this.removeDraft(this.activeDraftKey);
        this.resetPortalState({ preserveLookupValues: true });
      },

      removeDraft(key) {
        const storage = this.getStorage();

        if (!storage || !key) {
          return;
        }

        try {
          storage.removeItem(key);
        } catch (error) {
          return;
        }
      },

      readDraftByKey(key) {
        const storage = this.getStorage();

        if (!storage || !key) {
          return null;
        }

        try {
          const rawDraft = storage.getItem(key);

          if (!rawDraft) {
            return null;
          }

          const draft = JSON.parse(rawDraft);

          if (
            draft?.mode !== this.config?.mode ||
            helpers.isPortalDraftExpired(draft?.savedAt, getNow(), DRAFT_TTL_MS)
          ) {
            storage.removeItem(key);
            return null;
          }

          return draft;
        } catch (error) {
          storage.removeItem(key);
          return null;
        }
      },

      findLatestDraftForMode() {
        const storage = this.getStorage();
        const modePrefix = DRAFT_KEY_PREFIX + ':' + (this.config?.mode || 'return') + ':';

        if (!storage) {
          return null;
        }

        let latestDraft = null;

        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);

          if (!key || key.indexOf(modePrefix) !== 0) {
            continue;
          }

          const draft = this.readDraftByKey(key);

          if (!draft) {
            continue;
          }

          if (!latestDraft || Number(draft.savedAt) > Number(latestDraft.draft.savedAt)) {
            latestDraft = {
              key: key,
              draft: draft,
            };
          }
        }

        return latestDraft;
      },

      buildDraftItemLookup() {
        return this.items.reduce((draftItems, item) => {
          const successfulMedia = (item.media || [])
            .filter((entry) => entry.status === 'success' && entry.remoteUrl)
            .map((entry) => ({
              id: entry.id,
              name: entry.name,
              type: entry.type,
              kind: entry.kind,
              remoteUrl: entry.remoteUrl,
            }));
          const notes = normalizeString(item.notes);

          if (
            !item.reasonId &&
            !item.quantity &&
            !notes &&
            !successfulMedia.length
          ) {
            return draftItems;
          }

          draftItems[item.lineItemId] = {
            lineItemId: item.lineItemId,
            quantity: item.quantity || '',
            reasonId: item.reasonId || '',
            reasonLabel: item.reasonLabel || '',
            notes: notes,
            media: successfulMedia,
          };

          return draftItems;
        }, {});
      },

      buildDraftPayload() {
        if (!this.order) {
          return null;
        }

        return {
          version: 1,
          mode: this.config?.mode || 'return',
          savedAt: getNow(),
          orderNumber: normalizeString(this.formOrderNumber),
          zip: normalizeString(this.formZipCode),
          currentStep: helpers.sanitizePortalStep(this.currentStep),
          requestId: this.requestId,
          shippingChoice: this.shippingChoice,
          country: this.country,
          makeInitialShippingZero: this.makeInitialShippingZero,
          orderSnapshot: this.order,
          items: this.buildDraftItemLookup(),
        };
      },

      persistDraft() {
        const storage = this.getStorage();
        const draftKey = this.getCurrentDraftKey();
        const payload = this.buildDraftPayload();

        if (!storage || !draftKey || !payload) {
          return;
        }

        try {
          storage.setItem(draftKey, JSON.stringify(payload));
          this.activeDraftKey = draftKey;
        } catch (error) {
          return;
        }
      },

      revokeItemMedia(items) {
        (items || []).forEach((item) => {
          (item?.media || []).forEach((entry) => {
            if (
              entry?.previewUrl &&
              entry.previewUrl.indexOf('blob:') === 0
            ) {
              URL.revokeObjectURL(entry.previewUrl);
            }
          });
        });
      },

      resetPortalState(options) {
        const preserveLookupValues = options?.preserveLookupValues !== false;

        this.syncSuspended = true;

        try {
          this.revokeItemMedia(this.items);
          this.order = null;
          this.detailedOrder = null;
          this.items = [];
          this.requestId = '';
          this.currentStep = 1;
          this.lookupLoading = false;
          this.detailLoading = false;
          this.submitLoading = false;
          this.step2Attempted = false;
          this.lookupError = '';
          this.detailError = '';
          this.country = '';
          this.shippingChoice = 'need';
          this.makeInitialShippingZero = false;
          this.activeDraftKey = '';

          if (!preserveLookupValues) {
            this.formOrderNumber = '';
            this.formZipCode = '';
          }
        } finally {
          this.syncSuspended = false;
        }

        this.syncUrlState();
      },

      applyLookupOrderResponse(response) {
        const order = response?.data || {};

        this.order = order;
        this.detailedOrder = null;
        this.requestId = response?.request_id || this.requestId || '';
        this.country = order?.shippingAddress?.country || '';
        this.makeInitialShippingZero = Boolean(
          order?.initial_shipping_zero_without_return_label
        );
        this.items = order.lineItems.map((lineItem, index) => {
          const item = helpers.buildItemModel(lineItem, index);
          item.reasonLabel = '';
          return item;
        });
        this.updateCountryDefaults();
        this.step2Attempted = false;
        this.activeDraftKey = this.getCurrentDraftKey();
      },

      async restoreFromBrowser() {
        const urlState = this.readUrlState();

        if (urlState.hasLookupValues) {
          this.syncSuspended = true;

          try {
            this.formOrderNumber = urlState.orderNumber;
            this.formZipCode = urlState.zip;
            this.currentStep = 1;
          } finally {
            this.syncSuspended = false;
          }

          const matchingDraft = this.readDraftByKey(
            this.buildDraftKey(urlState.orderNumber, urlState.zip)
          );

          if (matchingDraft && urlState.step >= 2) {
            await this.restoreDraftWithLoader(matchingDraft, urlState.step);
            return;
          }

          this.currentStep = 1;
          this.syncUrlState();
          return;
        }

        const latestDraft = this.findLatestDraftForMode();

        if (latestDraft) {
          await this.restoreDraftWithLoader(
            latestDraft.draft,
            latestDraft.draft.currentStep
          );
          return;
        }

        this.syncUrlState();
      },

      async restoreDraftWithLoader(draft, requestedStep) {
        this.restoreLoading = true;

        try {
          await this.restoreDraft(draft, requestedStep);
        } finally {
          this.restoreLoading = false;
        }
      },

      async restoreDraft(draft, requestedStep) {
        if (!draft) {
          return;
        }

        this.syncSuspended = true;

        try {
          this.formOrderNumber = draft.orderNumber || this.formOrderNumber;
          this.formZipCode = draft.zip || this.formZipCode;
          this.shippingChoice = draft.shippingChoice || this.shippingChoice;
          this.requestId = draft.requestId || '';
          this.country = draft.country || '';
          this.makeInitialShippingZero = Boolean(draft.makeInitialShippingZero);
        } finally {
          this.syncSuspended = false;
        }

        try {
          const response = await this.requestOrder(true);

          this.syncSuspended = true;

          try {
            this.applyLookupOrderResponse(response);
            this.items = helpers.mergePortalDraftItems(
              this.items,
              draft.items,
              this.reasonsById
            );
            this.shippingChoice =
              this.isWarrantyMode
                ? 'need'
                : draft.shippingChoice ||
                  (normalizeString(this.country) === 'United States' ? 'need' : 'self');

            const sanitizedStep = helpers.sanitizePortalStep(requestedStep);

            if (sanitizedStep >= 4 || (sanitizedStep >= 3 && !this.usesEvidenceStep)) {
              const loaded = await this.ensureDetailedOrder();

              if (!loaded) {
                throw new Error('Unable to restore the review step.');
              }

              this.currentStep = 4;
            } else if (sanitizedStep === 3 && this.usesEvidenceStep) {
              this.currentStep = 3;
            } else if (sanitizedStep >= 2) {
              this.currentStep = 2;
            } else {
              this.currentStep = 1;
            }

            this.activeDraftKey = this.getCurrentDraftKey();
          } finally {
            this.syncSuspended = false;
          }

          this.persistDraft();
          this.syncUrlState();
        } catch (error) {
          this.removeDraft(
            this.buildDraftKey(draft.orderNumber || this.formOrderNumber, draft.zip || this.formZipCode)
          );
          this.resetPortalState({ preserveLookupValues: true });
        }
      },

      get isReturnMode() {
        return this.config?.mode === 'return';
      },

      get isWarrantyMode() {
        return this.config?.mode === 'warranty';
      },

      get orderName() {
        return this.order?.name || '';
      },

      get customerName() {
        const firstName = this.order?.shippingAddress?.firstName || '';
        const lastName = this.order?.shippingAddress?.lastName || '';

        return [firstName, lastName].filter(Boolean).join(' ');
      },

      get orderMeta() {
        return helpers.getOrderMeta(this.order);
      },

      get validation() {
        return helpers.getStep2Validation(
          this.config.mode,
          this.items,
          this.reasonsById,
          {
            warrantyDescriptionMinLength:
              this.config?.warrantyDescriptionMinLength || 100,
          }
        );
      },

      get selectedItems() {
        return helpers.getSelectedItems(this.items);
      },

      get selectedTotals() {
        return helpers.getSelectedTotals(this.items);
      },

      get manualReview() {
        return helpers.requiresManualReview(
          this.config.mode,
          this.items,
          this.reasonsById
        );
      },

      get usesEvidenceStep() {
        return this.manualReview;
      },

      get usesCompactReturnStepper() {
        return this.isReturnMode;
      },

      get evidenceStepLabel() {
        return this.isWarrantyMode ? 'Claim details' : 'Evidence';
      },

      get navSteps() {
        if (this.usesCompactReturnStepper) {
          return [
            { step: 1, label: 'Lookup', targetStep: 1 },
            { step: 2, label: 'Select items', targetStep: 2 },
            { step: 3, label: 'Review & submit', targetStep: this.currentStep >= 4 ? 4 : 3 },
          ];
        }

        return [
          { step: 1, label: 'Lookup', targetStep: 1 },
          { step: 2, label: 'Select items', targetStep: 2 },
          { step: 3, label: this.evidenceStepLabel, targetStep: 3 },
          { step: 4, label: 'Review & submit', targetStep: 4 },
        ];
      },

      get activeNavStep() {
        if (this.usesCompactReturnStepper) {
          return this.currentStep <= 2 ? this.currentStep : 3;
        }

        return this.currentStep;
      },

      get showShippingChoiceUi() {
        return false;
      },

      get showShippingSection() {
        if (this.isWarrantyMode) {
          return true;
        }

        return this.summary.showShippingChoice;
      },

      get refundRows() {
        return helpers.buildRefundRows(this.summary);
      },

      get totalsTitle() {
        return this.isWarrantyMode ? 'Warranty charges' : 'Estimated refund';
      },

      get shippingSummaryNote() {
        if (this.isWarrantyMode) {
          return '';
        }

        if (!this.summary.showShippingLabelCostRow || !this.showShippingChoiceUi) {
          return '';
        }

        return 'Shipping label cost only applies when you request a label from us.';
      },

      get canAdvanceStep2() {
        return this.validation.canContinue;
      },

      get currentTaxAmount() {
        return this.order?.currentTotalTaxSet?.presentmentMoney?.amount || 0;
      },

      get currentInitialShipmentCost() {
        return (
          this.detailedOrder?.initialShipmentCost?.amount ||
          this.order?.initialShipmentCost?.amount ||
          0
        );
      },

      get currentReturnShipmentCost() {
        return (
          this.detailedOrder?.returnShipmentCost?.amount ||
          this.order?.returnShipmentCost?.amount ||
          0
        );
      },

      get currentBaseShippingCost() {
        if (this.isWarrantyMode) {
          return this.config?.shipping?.warrantyShippingCost || 0;
        }

        if (this.country !== 'United States') {
          return 0;
        }

        return this.config?.shipping?.returnShippingCost || 0;
      },

      get summary() {
        const summaryArgs = {
          items: this.items,
          taxAmount: this.currentTaxAmount,
          initialShipmentCost: this.currentInitialShipmentCost,
          returnShipmentCost: this.currentReturnShipmentCost,
          baseShippingCost: this.currentBaseShippingCost,
          shippingChoice: this.shippingChoice,
          makeInitialShippingZero: this.makeInitialShippingZero,
          country: this.country,
        };

        if (this.isWarrantyMode) {
          return helpers.computeWarrantySummary(summaryArgs);
        }

        return helpers.computeReturnSummary(summaryArgs);
      },

      get hiddenFields() {
        return helpers.buildHiddenFields(
          this.config.mode,
          {
            order: this.order,
            items: this.items,
            requestId: this.requestId,
          },
          this.summary,
          {
            shipping: this.config.shipping,
          }
        );
      },

      get manualReviewHeading() {
        if (this.isWarrantyMode) {
          return 'Warranty claim details';
        }

        return helpers.getManualReviewHeading(
          this.config,
          this.selectedItems,
          this.reasonsById
        );
      },

      get manualReviewCopy() {
        if (!this.isWarrantyMode) {
          return '';
        }

        return this.config?.copy?.returnReasonGeneralHeading || '';
      },

      get finalSubmitLabel() {
        return this.isWarrantyMode
          ? 'Claim Warranty'
          : 'Submit return request';
      },

      navStepState(navStep) {
        const stepNumber = Number(navStep?.step || navStep);
        const activeStep = this.activeNavStep;

        if (
          !this.usesCompactReturnStepper &&
          stepNumber === 3 &&
          !this.usesEvidenceStep &&
          this.currentStep >= 4
        ) {
          return 'skipped';
        }

        if (stepNumber < activeStep) {
          return 'complete';
        }

        if (stepNumber === activeStep) {
          return 'active';
        }

        return 'upcoming';
      },

      canOpenNavStep(navStep) {
        const stepNumber = Number(navStep?.step || navStep);
        const targetStep = Number(navStep?.targetStep || stepNumber);

        if (
          !this.usesCompactReturnStepper &&
          stepNumber === 3 &&
          !this.usesEvidenceStep
        ) {
          return false;
        }

        return targetStep < this.currentStep;
      },

      openNavStep(navStep) {
        const normalizedTargetStep = Number(navStep?.targetStep || navStep?.step || navStep);

        if (
          !Number.isFinite(normalizedTargetStep) ||
          normalizedTargetStep === this.currentStep ||
          !this.canOpenNavStep(navStep)
        ) {
          return;
        }

        this.currentStep = normalizedTargetStep;
      },

      async lookupOrder() {
        this.lookupError = '';
        this.detailError = '';

        if (!this.formOrderNumber) {
          this.lookupError =
            this.config?.copy?.orderNumberRequiredMessage ||
            'Enter your order number so we can look up your order.';
          return;
        }

        if (!this.formZipCode) {
          this.lookupError =
            this.config?.copy?.zipCodeRequiredMessage ||
            'Enter the delivery ZIP code used on the order so we can verify it.';
          return;
        }

        this.lookupLoading = true;

        try {
          const response = await this.requestOrder(true);

          this.applyLookupOrderResponse(response);
          this.currentStep = 2;
          this.persistDraft();
          this.syncUrlState();
        } catch (error) {
          this.lookupError = getErrorMessage(
            error,
            this.config?.copy?.generalErrorMessage ||
              'Something went wrong while loading your order. Please try again.'
          );
        } finally {
          this.lookupLoading = false;
        }
      },

      async requestOrder(withoutRates) {
        const response = await this.fetchJson(this.buildOrderUrl(withoutRates));

        if (!response.success) {
          throw new Error(
            response?.errors?.[0] ||
              this.config?.copy?.orderNumberExistsMessage ||
              'Unable to find that order.'
          );
        }

        const order = response.data || {};

        if (
          !Object.keys(order).length ||
          !Array.isArray(order.lineItems) ||
          !order.lineItems.length
        ) {
          throw new Error(
            this.config?.copy?.orderNumberOtherMessage ||
              'The items in your order are not eligible.'
          );
        }

        return response;
      },

      async ensureDetailedOrder() {
        if (this.detailedOrder || this.detailLoading) {
          return Boolean(this.detailedOrder);
        }

        this.detailError = '';
        this.detailLoading = true;

        try {
          const response = await this.requestOrder(false);

          this.detailedOrder = response.data || {};
          this.makeInitialShippingZero = Boolean(
            this.detailedOrder?.initial_shipping_zero_without_return_label ||
              this.makeInitialShippingZero
          );
          return true;
        } catch (error) {
          this.detailError = getErrorMessage(
            error,
            this.config?.copy?.generalErrorMessage ||
              'Unable to load shipping details.'
          );
          return false;
        } finally {
          this.detailLoading = false;
        }
      },

      buildOrderUrl(withoutRates) {
        const params = new URLSearchParams({
          shop: this.config?.shopDomain || '',
          order_number: this.formOrderNumber || '',
          zip: this.formZipCode || '',
          type: this.config?.mode || 'return',
        });

        if (withoutRates) {
          params.set('without-rates', 'true');
        }

        return '/apps/return/api/orders/find?' + params.toString();
      },

      async fetchJson(url, options) {
        const response = await fetch(url, options);
        return await response.json();
      },

      updateCountryDefaults() {
        if (this.isWarrantyMode) {
          this.shippingChoice = 'need';
          return;
        }

        this.shippingChoice =
          normalizeString(this.country) === 'United States' ? 'need' : 'self';
      },

      selectReason(item, reasonId) {
        const nextReasonId =
          typeof reasonId === 'string' ? reasonId : normalizeString(item?.reasonId);
        const reason = helpers.getReasonById(this.reasonsById, nextReasonId);

        if (reason?.linkToWarranty && this.config?.warrantyPageUrl) {
          global.location.href = this.config.warrantyPageUrl;
          return;
        }

        item.reasonId = nextReasonId;
        item.reasonLabel = reason?.label || '';

        if (!reason?.requiresMedia) {
          this.clearMedia(item);
        }

        if (!reason?.requiresNotes && this.isReturnMode) {
          item.notes = '';
        }

        this.refreshItems();
      },

      updateQuantity(item, value) {
        const nextQuantity =
          typeof value === 'string' ? value : normalizeString(item?.quantity);

        item.quantity = nextQuantity ? String(nextQuantity) : '';
        this.refreshItems();
      },

      getReason(item) {
        return helpers.getReasonById(this.reasonsById, item.reasonId);
      },

      shouldShowNotes(item) {
        const reason = this.getReason(item);

        return Boolean(reason?.requiresNotes);
      },

      shouldShowMedia(item) {
        const reason = this.getReason(item);

        return Boolean(reason?.requiresMedia);
      },

      itemErrorList(item) {
        return this.validation.itemErrors[item.id] || [];
      },

      shouldShowItemErrors(item) {
        return this.step2Attempted || helpers.isItemEngaged(item);
      },

      itemCardDetails(item) {
        const hasMultipleOrdered = Number(item.maxQuantity) > 1;

        return helpers.buildItemDisplayDetails(item, {
          includeQuantity: hasMultipleOrdered,
          quantityLabel: 'Order quantity',
          quantityValue: item.maxQuantity,
          includeUnitPrice: hasMultipleOrdered,
          includeOrderTotal: true,
          includeSelectedTotal: true,
        });
      },

      reviewItemDetails(item) {
        const hasMultipleOrdered = Number(item.maxQuantity) > 1;

        return helpers.buildItemDisplayDetails(item, {
          includeQuantity: hasMultipleOrdered,
          includeUnitPrice: hasMultipleOrdered,
          includeSelectedTotal: true,
        });
      },

      formatMoney(value) {
        return helpers.formatMoney(value);
      },

      noteHint(item) {
        if (!this.isWarrantyMode) {
          return '';
        }

        const remaining =
          (this.config?.warrantyDescriptionMinLength || 100) -
          String(item.notes || '').trim().length;

        if (remaining <= 0) {
          return 'Minimum description met.';
        }

        return remaining + ' more characters required.';
      },

      mediaCounts(item) {
        return helpers.getMediaCounts(item);
      },

      hasMediaKind(item, kind) {
        const counts = this.mediaCounts(item);

        if (kind === 'video') {
          return counts.videos > 0;
        }

        return counts.images > 0;
      },

      warrantyMediaRequirementStatus(item, kind) {
        const label = kind === 'video' ? 'video' : 'image';

        if (this.hasMediaKind(item, kind)) {
          return label.charAt(0).toUpperCase() + label.slice(1) + ' added';
        }

        return 'Still need 1 ' + label;
      },

      mediaHint(item) {
        if (!this.shouldShowMedia(item)) {
          return '';
        }

        const counts = this.mediaCounts(item);

        if (this.isWarrantyMode) {
          if (!counts.images && !counts.videos) {
            return 'Upload at least 1 image and 1 video to complete your warranty proof.';
          }

          if (!counts.images) {
            return 'Video added. Upload 1 image to complete your warranty proof.';
          }

          if (!counts.videos) {
            return 'Image added. Upload 1 video to complete your warranty proof.';
          }

          return (
            'Warranty proof received: ' +
            counts.images +
            ' image(s) and ' +
            counts.videos +
            ' video(s) uploaded.'
          );
        }

        const total = counts.images + counts.videos + counts.other;
        return total + ' file(s) uploaded';
      },

      successfulMedia(item) {
        return (item?.media || []).filter((entry) => entry.status === 'success');
      },

      hasSuccessfulMedia(item) {
        return this.successfulMedia(item).length > 0;
      },

      createMediaEntry(file) {
        const mimeType = file.type || 'application/octet-stream';
        const mediaEntry = {
          id: uniqueId('upload'),
          name: file.name,
          type: mimeType,
          kind: mimeType.indexOf('video/') === 0 ? 'video' : 'image',
          previewUrl: URL.createObjectURL(file),
          remoteUrl: '',
          status: 'queued',
          error: '',
          file: file,
        };

        if (global.Alpine?.reactive) {
          return global.Alpine.reactive(mediaEntry);
        }

        return mediaEntry;
      },

      async handleFilesChange(item, event) {
        const files = Array.from(event?.target?.files || []);

        if (!files.length) {
          return;
        }

        for (const file of files) {
          const entry = this.createMediaEntry(file);
          item.media.push(entry);
          this.refreshItems();
          await this.uploadMediaEntry(entry);
        }

        event.target.value = '';
      },

      async uploadMediaEntry(entry) {
        entry.status = 'uploading';
        entry.error = '';
        this.refreshItems();

        try {
          const signedUrlResponse = await fetch(
            '/apps/return/api/file-upload/signed-url',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                filename: entry.file.name,
                contentType: entry.file.type,
              }),
            }
          );

          if (!signedUrlResponse.ok) {
            throw new Error('Failed to get an upload URL.');
          }

          const payload = await signedUrlResponse.json();
          const uploadResponse = await fetch(payload.signedUrl, {
            method: 'PUT',
            body: entry.file,
            headers: {
              'x-amz-acl': 'public-read',
              'Content-Type': entry.file.type || 'application/octet-stream',
            },
          });

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload the file.');
          }

          entry.remoteUrl = payload.publicUrl || '';
          entry.status = 'success';
          this.refreshItems();
        } catch (error) {
          entry.status = 'error';
          entry.error = getErrorMessage(
            error,
            'Upload failed. Please try again.'
          );
          this.refreshItems();
        }
      },

      async retryMedia(entry) {
        if (!entry?.file) {
          return;
        }

        await this.uploadMediaEntry(entry);
      },

      removeMedia(item, entryId) {
        const index = item.media.findIndex((entry) => entry.id === entryId);

        if (index === -1) {
          return;
        }

        const [entry] = item.media.splice(index, 1);

        if (entry?.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
        }

        this.refreshItems();
      },

      clearMedia(item) {
        (item.media || []).forEach((entry) => {
          if (entry?.previewUrl) {
            URL.revokeObjectURL(entry.previewUrl);
          }
        });
        item.media = [];
        this.refreshItems();
      },

      async continueFromItems() {
        this.step2Attempted = true;

        if (!this.validation.canContinue) {
          return;
        }

        this.detailError = '';

        if (this.usesEvidenceStep) {
          this.currentStep = 3;
          return;
        }

        const loaded = await this.ensureDetailedOrder();

        if (!loaded) {
          return;
        }

        this.currentStep = 4;
      },

      async continueFromEvidence() {
        this.step2Attempted = true;

        if (!this.validation.canContinue || this.detailLoading) {
          return;
        }

        const loaded = await this.ensureDetailedOrder();

        if (!loaded) {
          return;
        }

        this.currentStep = 4;
      },

      async submitReview() {
        this.step2Attempted = true;

        if (!this.validation.canContinue || this.submitLoading) {
          return;
        }

        this.submitLoading = true;

        await this.$nextTick();
        this.$root.closest('form').submit();
      },

      selectedItemLineTotal(item) {
        return helpers.formatMoney(Number(item.quantity) * Number(item.unitPrice));
      },
    };
  }

  function register() {
    if (!global.Alpine || !helpers || global.__returnsPortalComponentRegistered) {
      return;
    }

    global.__returnsPortalComponentRegistered = true;
    global.Alpine.data('returnsPortalComponent', createPortal);
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('alpine:init', register);
  }

  global.returnsPortalComponent = createPortal;

  if (global.Alpine) {
    register();
  }

  return {
    createPortal: createPortal,
    register: register,
  };
});
